import * as React from "react";
import { FvcSession, FvcTest } from "webportalclient";
import { CommentsDialog } from "../../../misc/components/CommentsSelector";
import { SessionFixedCommentState, TestFixedCommentsState } from "../../Shared/UseFixedComments";
import { NextGenSessionFixedCommentState, NextGenTestFixedCommentState } from "./UseNextGenFixedComments";
import { DynamicResultsGroupHeading } from "../../Shared/ResultsGroupHeading";

export interface TestCommentsCount {
    id: string;
    count: number;
}

interface NextGenFixedCommentSelectorsProps {
    headerPrefix: string;
    editable: boolean;
    session: FvcSession;
    updatedSession?: FvcSession;
    sessionFixedComments: NextGenSessionFixedCommentState;
    testFixedComments: NextGenTestFixedCommentState[];
    newTestFixedComments?: NextGenTestFixedCommentState[];
    onTestFixedCommentClick: (id: string) => void;
    onSessionFixedCommentClick: () => void;
    handleSessionChange: (commentId: string, checked: boolean) => void;
    handleTestChange: (testId: string, commentId: string, checked: boolean) => void;
    handleSessionClose: () => void;
    handleTestClose: (testId: string) => void;
}

interface RenderSessionModalProps {
    editable: boolean;
    headerPrefix: string;
    sessionFixedComments: NextGenSessionFixedCommentState;
    isPageValid: boolean;
    handleChange: (id: string, checked: boolean) => void;
    handleClose: () => void;
}

interface RenderTestModalProps {
    editable: boolean;
    headerPrefix: string;
    testFixedComments: NextGenTestFixedCommentState;
    isPageValid: boolean;
    handleChange: (id: string, checked: boolean) => void;
    handleClose: () => void;
}

interface NextGenFixedCommentSelectorButtonProps {
    tests: FvcTest[];
    selectedTestCommentsCount: TestCommentsCount[];
    onTestFixedCommentClick: (id: string) => void;
}

const RenderSessionModal = ({ editable, headerPrefix, sessionFixedComments, isPageValid, handleChange, handleClose }: RenderSessionModalProps) => {
    return (<CommentsDialog
        show={sessionFixedComments.isVisible}
        header={`${headerPrefix} Fixed Comments - Session`}
        editable={editable}
        items={sessionFixedComments.comments}
        errorMessage={!isPageValid ? "A session comment is required" : null}
        onChange={handleChange}
        onClose={handleClose}
    />);
}

const RenderTestModal = ({ editable, headerPrefix, testFixedComments, isPageValid, handleChange, handleClose }: RenderTestModalProps) => {
    return (<CommentsDialog
        show={testFixedComments.isVisible}
        header={`${headerPrefix} Fixed Comments - Test`}
        editable={editable}
        items={testFixedComments.comments}
        errorMessage={!isPageValid ? "A test comment is required" : null}
        onChange={handleChange}
        onClose={handleClose}
    />);
}

export const NextGenFixedCommentSelectors = ({ headerPrefix, editable, session, sessionFixedComments, testFixedComments, newTestFixedComments, onTestFixedCommentClick, onSessionFixedCommentClick, handleSessionClose, handleTestClose, handleSessionChange, handleTestChange }: NextGenFixedCommentSelectorsProps) => {
    const [selectedTestCommentsCount, setSelectedTestCommentsCount] = React.useState<TestCommentsCount[]>();

    React.useEffect(() => {
        setSelectedTestCommentsCount(testFixedComments.map(x => ({
            id: x.testId,
            count: x.comments.filter(x => x.selected).length
        })));
    }, [testFixedComments]);

    const selectedSessionCommentsCount = sessionFixedComments.comments.filter(
        x => x.selected
    ).length;

    return (
        <>
            <tr>
                <td>{headerPrefix} Comments</td>
                {session.tests.sort((a, b) => a.index - b.index).map(t => {
                    return (
                        <td key={`FixedCommentsButton${t.id}`}>
                            <button
                                data-automationid={`open-test-fixed-comments-${t.index}`}
                                className="btn btn-primary"
                                onClick={() => onTestFixedCommentClick(t.id)}>
                                Test Comments&nbsp;
                                <span className="badge badge-info">
                                    {selectedTestCommentsCount && selectedTestCommentsCount.length > 0 && selectedTestCommentsCount.find(x => x.id == t.id) ? selectedTestCommentsCount.find(x => x.id == t.id).count : null}
                                </span>
                            </button>
                        </td>
                    );
                })}
                <td>
                    <button className="btn btn-primary" data-automationid="open-session-fixed-comments" onClick={() => onSessionFixedCommentClick()}>
                        Session Comments&nbsp;
                        <span className="badge badge-info">{selectedSessionCommentsCount}</span>
                    </button>
                    {testFixedComments.map(tfc => (
                        <RenderTestModal key={`fixed-comments-modal-${tfc.testId}`} headerPrefix={headerPrefix} editable={editable} isPageValid={true} testFixedComments={tfc} handleChange={(id, checked) => handleTestChange(tfc.testId, id, checked)} handleClose={() => handleTestClose(tfc.testId)} />
                    ))}
                    <RenderSessionModal headerPrefix={headerPrefix} editable={editable} isPageValid={true} sessionFixedComments={sessionFixedComments} handleChange={(id, checked) => handleSessionChange(id, checked)} handleClose={handleSessionClose} />
                </td>
            </tr>
        </>
    );
}